import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CallMadeIcon from '@mui/icons-material/CallMade'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { heapEvent } from '@equipmentshare/heaper'
import {
	useInventoryLocationsQuery,
	useMobileToolTrailerInventoryItemsQuery,
	useMobileToolTrailerTransferOrdersQuery,
	useUnloadMobileToolTrailerMutation,
} from '../../graphql'
import { useShowSuccess } from '../../hooks/useToast'
import { formatDateTime } from '../../utils/formatters/date'
import { useDcAdminContext } from './contexts/DcAdminContext'
import TableHeaderCell from './components/TableHeaderCell'
import parseUnloadQuantity, { type UnloadQuantityResult } from './parseUnloadQuantity'

type SortDir = 'asc' | 'desc'
type AssetSortField = 'make' | 'model'
type PartSortField = 'partNumber' | 'description'

type AssetItem = {
	id: string,
	equipmentMakeId: string | null,
	equipmentModelId: string | null,
	makeName: string,
	modelName: string,
	serialNumber: string | null,
	name: string | null,
}

type AssetGroup = {
	key: string,
	makeName: string,
	modelName: string,
	assets: AssetItem[],
}

type StorePartItem = {
	id: string,
	productId: string,
	partNumber: string,
	description: string,
	quantity: number,
	availableQuantity: number,
	reservedQuantity: number,
	outgoingQuantity: number,
}

type ToPaging = {
	first?: number,
	last?: number,
	after?: string,
	before?: string,
}

const PAGE_SIZE = 10
const TERMINAL_STATUSES = [ 'COMPLETED', 'CANCELLED', 'REJECTED' ]

function compareStr( a: string, b: string, dir: SortDir ): number {
	const result = a.localeCompare( b, undefined, { numeric: true, sensitivity: 'base' } )
	return dir === 'asc' ? result : -result
}

function groupAssets( assets: AssetItem[] ): AssetGroup[] {
	const groups = new Map<string, AssetGroup>()
	assets.forEach( ( asset ) => {
		const key = `${asset.equipmentMakeId ?? 'none'}::${asset.equipmentModelId ?? 'none'}`
		const existing = groups.get( key )
		if ( existing ) {
			existing.assets.push( asset )
		} else {
			groups.set( key, {
				key,
				makeName: asset.makeName,
				modelName: asset.modelName,
				assets: [ asset ],
			} )
		}
	} )
	return Array.from( groups.values() )
}

function sortGroups(
	groups: AssetGroup[],
	field: AssetSortField,
	dir: SortDir,
): AssetGroup[] {
	return [ ...groups ].sort( ( a, b ) => {
		const primary = field === 'make'
			? compareStr( a.makeName, b.makeName, dir )
			: compareStr( a.modelName, b.modelName, dir )
		if ( primary !== 0 ) {
			return primary
		}
		// Secondary key is the other identifier, always ascending.
		return field === 'make'
			? compareStr( a.modelName, b.modelName, 'asc' )
			: compareStr( a.makeName, b.makeName, 'asc' )
	} )
}

function isTransferOrderInFlight( node: { status: string, assetTransferStatus?: string | null } ): boolean {
	return !TERMINAL_STATUSES.includes( node.status ) || node.assetTransferStatus === 'PROCESSING'
}

function StatusChip( { status }: { status: string } ) {
	const color = status === 'COMPLETED'
		? 'success'
		: status === 'CANCELLED' || status === 'REJECTED'
			? 'default'
			: 'info'
	return <Chip size="small" variant="outlined" color={color} label={status.replace( /_/g, ' ' )} />
}

function DirectionChip( { isLoad }: { isLoad: boolean } ) {
	// "Load" pulls inventory onto the trailer (DC → MTT); "Unload" pushes it off
	// (MTT → DC). Unload reuses the page's warning accent — the same color as the
	// Unload action — so the operation that happens here reads at a glance.
	return isLoad ? (
		<Chip size="small" variant="outlined" color="info" icon={<CallReceivedIcon />} label="Load" />
	) : (
		<Chip size="small" variant="outlined" color="warning" icon={<CallMadeIcon />} label="Unload" />
	)
}

function AssetLegChip( { status }: { status: string } ) {
	if ( status === 'PROCESSING' ) {
		return (
			<Chip
				size="small"
				color="info"
				icon={<CircularProgress size={12} color="inherit" sx={{ ml: 0.5 }} />}
				label="Assets processing"
			/>
		)
	}
	const color = status === 'COMPLETED' ? 'success' : 'error'
	return <Chip size="small" variant="outlined" color={color} label={`Assets ${status.toLowerCase()}`} />
}

export default function MttDetails() {
	const { branchId } = useParams<{ branchId: string }>()
	const showSuccess = useShowSuccess()
	const { selectedDcId, dcs } = useDcAdminContext()

	const [ search, setSearch ] = useState( '' )
	const [ expandedGroups, setExpandedGroups ] = useState<Record<string, boolean>>( {} )
	// Nothing starts selected and no quantity is pre-filled: trailer counts aren't
	// reliable, so every unload must reflect what was actually counted.
	const [ selectedAssetIds, setSelectedAssetIds ] = useState<Set<string>>( new Set() )
	const [ productQuantities, setProductQuantities ] = useState<Record<string, string>>( {} )
	const [ touchedProductIds, setTouchedProductIds ] = useState<Set<string>>( new Set() )
	const [ showAllQuantityErrors, setShowAllQuantityErrors ] = useState( false )
	const [ confirmOpen, setConfirmOpen ] = useState( false )
	const [ submitError, setSubmitError ] = useState<string | null>( null )

	const destinationDc = dcs.find( ( dc ) => dc.id === selectedDcId )
	const destinationDcName = destinationDc?.name ?? 'the selected Distribution Center'

	const [ assetSort, setAssetSort ] = useState<{ field: AssetSortField, dir: SortDir }>( {
		field: 'make',
		dir: 'asc',
	} )
	const [ partSort, setPartSort ] = useState<{ field: PartSortField, dir: SortDir }>( {
		field: 'partNumber',
		dir: 'asc',
	} )
	const [ toPaging, setToPaging ] = useState<ToPaging>( { first: PAGE_SIZE } )

	const { data: locData, loading: locLoading } = useInventoryLocationsQuery( {
		variables: { branchIds: [ branchId ?? '' ], paging: { first: 10 } },
		skip: !branchId,
	} )

	const locationNode = locData?.inventoryLocations.edges[ 0 ]?.node
	const inventoryLocationId = locationNode?.id
	const mttName = locationNode?.branch?.name ?? locationNode?.name ?? 'MTT'

	const {
		data: itemsData,
		loading: itemsLoading,
		error: itemsError,
		refetch: refetchItems,
	} = useMobileToolTrailerInventoryItemsQuery( {
		variables: { inventoryLocationId: inventoryLocationId ?? '' },
		skip: !inventoryLocationId,
		fetchPolicy: 'network-only',
	} )

	const [ unloadMtt, { loading: mutLoading } ] = useUnloadMobileToolTrailerMutation()

	const { assets, storeParts } = useMemo( () => {
		const assetItems: AssetItem[] = []
		const storePartItems: StorePartItem[] = []
		const items = itemsData?.mobileToolTrailerInventoryItems ?? []
		items.forEach( ( item: any ) => {
			if ( item.__typename === 'Asset' ) {
				assetItems.push( {
					id: item.id,
					equipmentMakeId: item.equipmentMakeId ?? null,
					equipmentModelId: item.equipmentModelId ?? null,
					makeName: item.equipmentMake?.name ?? 'Unknown make',
					modelName: item.equipmentModel?.name ?? 'Unknown model',
					serialNumber: item.serialNumber ?? null,
					name: item.name ?? null,
				} )
			} else if ( item.__typename === 'StorePart' ) {
				storePartItems.push( {
					id: String( item.storePartId ),
					productId: String( item.part.id ),
					partNumber: item.part.partNumber ?? '',
					description: item.part.name ?? '',
					quantity: item.quantity,
					availableQuantity: item.availableQuantity,
					reservedQuantity: item.reservedQuantity,
					outgoingQuantity: item.outgoingQuantity,
				} )
			}
		} )
		return { assets: assetItems, storeParts: storePartItems }
	}, [ itemsData ] )

	const assetGroups = useMemo( () => groupAssets( assets ), [ assets ] )

	const isAssetSelected = ( id: string ) => selectedAssetIds.has( id )

	const toggleAsset = ( id: string ) => {
		setSelectedAssetIds( ( prev ) => {
			const next = new Set( prev )
			if ( next.has( id ) ) {
				next.delete( id )
			} else {
				next.add( id )
			}
			return next
		} )
	}

	const toggleGroup = ( group: AssetGroup, selectAll: boolean ) => {
		setSelectedAssetIds( ( prev ) => {
			const next = new Set( prev )
			group.assets.forEach( ( a ) => {
				if ( selectAll ) {
					next.add( a.id )
				} else {
					next.delete( a.id )
				}
			} )
			return next
		} )
	}

	const toggleExpanded = ( key: string ) => {
		setExpandedGroups( ( prev ) => ( { ...prev, [ key ]: !prev[ key ] } ) )
	}

	const getProductQuantityInput = ( part: StorePartItem ) => productQuantities[ part.productId ] ?? ''

	// Blank or 0 leaves a row on the trailer. Anything else that isn't a whole
	// number from 1 to available is an error the user must fix before unloading.
	// Rows with outgoing quantity can't be unloaded, so their input is ignored.
	const getQuantityResult = ( part: StorePartItem ): UnloadQuantityResult =>
		( part.outgoingQuantity > 0
			? { status: 'empty' }
			: parseUnloadQuantity( getProductQuantityInput( part ), part.availableQuantity ) )

	// Errors appear once a field is blurred (or the user tries to unload), then
	// update as they type so a corrected value clears its error immediately.
	const getVisibleQuantityError = ( part: StorePartItem ): string | null => {
		const result = getQuantityResult( part )
		const shown = showAllQuantityErrors || touchedProductIds.has( part.productId )
		return result.status === 'invalid' && shown ? result.message : null
	}

	const setProductQuantity = ( part: StorePartItem, value: string ) => {
		setProductQuantities( ( prev ) => ( { ...prev, [ part.productId ]: value } ) )
	}

	const touchProductQuantity = ( part: StorePartItem ) => {
		setTouchedProductIds( ( prev ) => new Set( prev ).add( part.productId ) )
	}

	const lowerSearch = search.trim().toLowerCase()
	const matchesSearch = ( ...fields: ( string | null )[] ) =>
		!lowerSearch || fields.some( ( f ) => ( f ?? '' ).toLowerCase().includes( lowerSearch ) )

	const filteredAssetGroups = sortGroups( assetGroups, assetSort.field, assetSort.dir )
		.map( ( group ) => ( {
			...group,
			assets: group.assets
				.filter( ( a ) => matchesSearch( group.makeName, group.modelName, a.serialNumber, a.name, a.id ) )
				// Inner asset list is not user-sortable: order by ID, then serial number.
				.sort( ( a, b ) =>
					compareStr( a.id, b.id, 'asc' ) ||
					compareStr( a.serialNumber ?? '', b.serialNumber ?? '', 'asc' ) ),
		} ) )
		.filter( ( group ) => group.assets.length > 0 )

	const sortedStoreParts = [ ...storeParts ]
		.sort( ( a, b ) => compareStr( a[ partSort.field ], b[ partSort.field ], partSort.dir ) )
	const filteredStoreParts = sortedStoreParts.filter( ( p ) => matchesSearch( p.partNumber, p.description ) )

	const selectedAssets = assets.filter( ( a ) => isAssetSelected( a.id ) )
	const selectedAssetIdList = selectedAssets.map( ( a ) => a.id )

	// Table order, so the confirm dialog reads in the same order as the table.
	const selectedParts = sortedStoreParts.flatMap( ( p ) => {
		const result = getQuantityResult( p )
		return result.status === 'valid' ? [ { part: p, quantity: result.value } ] : []
	} )
	const selectedProducts = selectedParts.map( ( { part, quantity } ) => ( { productId: part.productId, quantity } ) )

	// In table order, so "fix" moves focus to the first problem the user sees.
	const invalidParts = sortedStoreParts.filter( ( p ) => getQuantityResult( p ).status === 'invalid' )
	const invalidCount = invalidParts.length

	const hasSelection = selectedAssetIdList.length > 0 || selectedProducts.length > 0

	const quantityInputId = ( part: StorePartItem ) => `mtt-unload-qty-${part.productId}`

	const revealQuantityErrors = () => {
		const [ firstInvalid ] = invalidParts
		setShowAllQuantityErrors( true )
		// A search can hide the bad row; clear it so the field can take focus.
		if ( firstInvalid && !filteredStoreParts.includes( firstInvalid ) ) {
			setSearch( '' )
		}
		requestAnimationFrame( () => {
			if ( firstInvalid ) {
				document.getElementById( quantityInputId( firstInvalid ) )?.focus()
			}
		} )
	}

	const handleSortAsset = ( field: AssetSortField ) => {
		setAssetSort( ( prev ) => (
			prev.field === field
				? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
				: { field, dir: 'asc' }
		) )
	}

	const handleSortPart = ( field: PartSortField ) => {
		setPartSort( ( prev ) => (
			prev.field === field
				? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
				: { field, dir: 'asc' }
		) )
	}

	const resetSelection = () => {
		setSelectedAssetIds( new Set() )
		setProductQuantities( {} )
		setTouchedProductIds( new Set() )
		setShowAllQuantityErrors( false )
	}

	// -----------------------------------------------------------------------
	// Recent transfer orders that move inventory between this trailer and the
	// selected DC — in either direction. With a DC selected we scope both the
	// from- and to-filters to {MTT, DC}, so the API returns MTT → DC (unloads)
	// and DC → MTT (loads). Without a DC we fall back to every unload off the
	// trailer. Polls while any order is still in flight so status stays live.
	// -----------------------------------------------------------------------
	const transferOrderEndpointIds = useMemo( () => {
		if ( !inventoryLocationId ) {
			return undefined
		}
		return selectedDcId ? [ inventoryLocationId, selectedDcId ] : [ inventoryLocationId ]
	}, [ inventoryLocationId, selectedDcId ] )

	const {
		data: toData,
		loading: toLoading,
		refetch: refetchTransferOrders,
		startPolling,
		stopPolling,
	} = useMobileToolTrailerTransferOrdersQuery( {
		variables: {
			fromInventoryIds: transferOrderEndpointIds,
			// Only constrain the destination once a DC is chosen — that pairing is
			// what limits results to loads/unloads between the trailer and the DC.
			toInventoryIds: selectedDcId ? transferOrderEndpointIds : undefined,
			paging: toPaging,
		},
		skip: !inventoryLocationId,
		fetchPolicy: 'cache-and-network',
	} )

	const toEdges = toData?.transferOrders.edges ?? []
	const toPageInfo = toData?.transferOrders.pageInfo
	const anyInFlight = toEdges.some( ( edge ) => isTransferOrderInFlight( edge.node ) )

	// Keep in-transit orders fresh: poll only while something is still moving.
	useEffect( () => {
		if ( anyInFlight ) {
			startPolling( 5000 )
		} else {
			stopPolling()
		}
		return () => stopPolling()
	}, [ anyInFlight, startPolling, stopPolling ] )

	const goNextPage = () => {
		if ( toPageInfo?.endCursor ) {
			setToPaging( { first: PAGE_SIZE, after: toPageInfo.endCursor } )
		}
	}

	const goPrevPage = () => {
		if ( toPageInfo?.startCursor ) {
			setToPaging( { last: PAGE_SIZE, before: toPageInfo.startCursor } )
		}
	}

	const handleConfirm = async() => {
		if ( !inventoryLocationId || !selectedDcId ) {
			return
		}
		setSubmitError( null )
		try {
			const result = await unloadMtt( {
				variables: {
					input: {
						fromInventoryLocationId: inventoryLocationId,
						toInventoryLocationId: selectedDcId,
						assetIds: selectedAssetIdList,
						products: selectedProducts,
					},
				},
			} )
			const payload = result.data?.unloadMobileToolTrailer
			if ( payload?.success ) {
				heapEvent( 'Inventory - DC Admin - MTT - Unload - Success', {
					mtt_branch_id: branchId ?? '',
					to_dc_id: selectedDcId,
					asset_count: selectedAssetIdList.length,
					product_count: selectedProducts.length,
				} )
				setConfirmOpen( false )
				resetSelection()
				showSuccess( 'mtt-unload', 'Transfer order created. Remaining inventory updated below.' )
				// Stay on the page: refresh what's left and surface the new transfer order.
				setToPaging( { first: PAGE_SIZE } )
				refetchItems()
				refetchTransferOrders()
			} else {
				setSubmitError( payload?.message ?? 'Unload failed. Please try again.' )
			}
		} catch ( err ) {
			setSubmitError( 'An unexpected error occurred. Please try again.' )
		}
	}

	const loading = locLoading || itemsLoading

	return (
		<Box sx={{ p: 3, maxWidth: 1100 }}>
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
				<Button
					component={Link}
					to="/dc-admin/mtt"
					startIcon={<ArrowBackIcon />}
					size="small"
					color="inherit"
				>
					Back
				</Button>
			</Stack>

			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
				<LocalShippingIcon sx={{ fontSize: 28, color: 'primary.main' }} />
				<Box>
					<Typography variant="h5" fontWeight={700}>{mttName}</Typography>
					<Typography variant="body2" color="text.secondary">
						Count items as they come off the trailer. Check each asset and enter the counted quantity for each bulk item, then unload to a Distribution Center.
					</Typography>
				</Box>
			</Stack>

			{loading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
					<CircularProgress />
				</Box>
			)}

			{!locLoading && !inventoryLocationId && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No inventory location found for this MTT.
				</Alert>
			)}

			{itemsError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					Failed to load MTT inventory: {itemsError.message}
				</Alert>
			)}

			{!loading && inventoryLocationId && !itemsError && (
				<>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 2 }}
					>
						<TextField
							size="small"
							label="Search inventory"
							value={search}
							onChange={( e ) => setSearch( e.target.value )}
							sx={{ minWidth: 280 }}
						/>
						<Stack direction="row" alignItems="center" spacing={1.5}>
							{!selectedDcId && (
								<Typography variant="caption" color="text.secondary">
									Select a Distribution Center in the header to unload.
								</Typography>
							)}
							{invalidCount > 0 ? (
								// Kept enabled on purpose: a disabled button can't explain itself.
								// Clicking it shows every error and focuses the first one.
								<Button
									variant="contained"
									color="error"
									startIcon={<ErrorOutlineIcon />}
									onClick={revealQuantityErrors}
								>
									Fix {invalidCount} {invalidCount === 1 ? 'quantity' : 'quantities'}
								</Button>
							) : (
								<Button
									variant="contained"
									color="warning"
									disabled={!hasSelection || !selectedDcId}
									onClick={() => {
										setSubmitError( null )
										setConfirmOpen( true )
									}}
								>
									Unload
								</Button>
							)}
						</Stack>
					</Stack>

					{assets.length === 0 && storeParts.length === 0 && (
						<Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
							<Typography color="text.secondary">This MTT has no inventory.</Typography>
						</Paper>
					)}

					{assets.length > 0 && (
						<Box sx={{ mb: 4 }}>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
								Assets
							</Typography>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow sx={{ bgcolor: 'grey.50' }}>
											<TableCell padding="checkbox" />
											<TableCell sx={{ width: 40 }} />
											<TableHeaderCell>
												<TableSortLabel
													active={assetSort.field === 'make'}
													direction={assetSort.field === 'make' ? assetSort.dir : 'asc'}
													onClick={() => handleSortAsset( 'make' )}
												>
													Make
												</TableSortLabel>
											</TableHeaderCell>
											<TableHeaderCell>
												<TableSortLabel
													active={assetSort.field === 'model'}
													direction={assetSort.field === 'model' ? assetSort.dir : 'asc'}
													onClick={() => handleSortAsset( 'model' )}
												>
													Model
												</TableSortLabel>
											</TableHeaderCell>
											<TableHeaderCell align="right">Count</TableHeaderCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredAssetGroups.map( ( group ) => {
											const selectedCount = group.assets.filter( ( a ) => isAssetSelected( a.id ) ).length
											const allSelected = selectedCount === group.assets.length
											const someSelected = selectedCount > 0 && !allSelected
											const expanded = !!expandedGroups[ group.key ]
											return (
												<Fragment key={group.key}>
													<TableRow hover>
														<TableCell padding="checkbox">
															<Checkbox
																checked={allSelected}
																indeterminate={someSelected}
																onChange={( e ) => toggleGroup( group, e.target.checked )}
															/>
														</TableCell>
														<TableCell>
															<IconButton size="small" onClick={() => toggleExpanded( group.key )}>
																{expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
															</IconButton>
														</TableCell>
														<TableCell>{group.makeName}</TableCell>
														<TableCell>{group.modelName}</TableCell>
														<TableCell align="right">
															{selectedCount} / {group.assets.length}
														</TableCell>
													</TableRow>
													<TableRow>
														<TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
															<Collapse in={expanded} timeout="auto" unmountOnExit>
																<Table size="small" sx={{ my: 1 }}>
																	<TableHead>
																		<TableRow>
																			<TableCell padding="checkbox" sx={{ width: 48 }} />
																			<TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
																			<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
																			<TableCell sx={{ fontWeight: 600 }}>Serial</TableCell>
																		</TableRow>
																	</TableHead>
																	<TableBody>
																		{group.assets.map( ( asset ) => (
																			<TableRow key={asset.id}>
																				<TableCell padding="checkbox" sx={{ width: 48 }}>
																					<Checkbox
																						size="small"
																						checked={isAssetSelected( asset.id )}
																						onChange={() => toggleAsset( asset.id )}
																					/>
																				</TableCell>
																				<TableCell>{asset.id}</TableCell>
																				<TableCell>{asset.name ?? '—'}</TableCell>
																				<TableCell>{asset.serialNumber ?? '—'}</TableCell>
																			</TableRow>
																		) )}
																	</TableBody>
																</Table>
															</Collapse>
														</TableCell>
													</TableRow>
												</Fragment>
											)
										} )}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}

					{storeParts.length > 0 && (
						<Box>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
								Bulk + Consumable
							</Typography>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow sx={{ bgcolor: 'grey.50' }}>
											<TableHeaderCell>
												<TableSortLabel
													active={partSort.field === 'partNumber'}
													direction={partSort.field === 'partNumber' ? partSort.dir : 'asc'}
													onClick={() => handleSortPart( 'partNumber' )}
												>
													Part #
												</TableSortLabel>
											</TableHeaderCell>
											<TableHeaderCell>
												<TableSortLabel
													active={partSort.field === 'description'}
													direction={partSort.field === 'description' ? partSort.dir : 'asc'}
													onClick={() => handleSortPart( 'description' )}
												>
													Description
												</TableSortLabel>
											</TableHeaderCell>
											<TableHeaderCell align="right">Available</TableHeaderCell>
											<TableHeaderCell align="right">Reserved</TableHeaderCell>
											<TableHeaderCell align="right">Outgoing</TableHeaderCell>
											<TableHeaderCell align="right">Qty to Unload</TableHeaderCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredStoreParts.map( ( part ) => {
											const disabled = part.outgoingQuantity > 0
											const quantityError = getVisibleQuantityError( part )
											const reservedFlag = part.availableQuantity < part.quantity
											return (
												<TableRow
													key={part.id}
													hover
													sx={disabled ? { opacity: 0.5, bgcolor: 'grey.50' } : undefined}
												>
													<TableCell>{part.partNumber || '—'}</TableCell>
													<TableCell>
														<Stack direction="row" spacing={1} alignItems="center">
															<span>{part.description || '—'}</span>
															{disabled && (
																<Chip
																	size="small"
																	color="warning"
																	label="Outgoing — cannot unload"
																/>
															)}
															{reservedFlag && !disabled && (
																<Tooltip title="Some quantity is reserved; only available quantity can be unloaded.">
																	<WarningAmberIcon
																		fontSize="small"
																		color="warning"
																	/>
																</Tooltip>
															)}
														</Stack>
													</TableCell>
													<TableCell align="right">{part.availableQuantity}</TableCell>
													<TableCell align="right">{part.reservedQuantity}</TableCell>
													<TableCell align="right">{part.outgoingQuantity}</TableCell>
													<TableCell align="right">
														{/* Text + numeric keypad rather than type="number": a number
														    input reports partial values like "-" as "", which would
														    silently drop a bad count instead of flagging it. */}
														<TextField
															id={quantityInputId( part )}
															size="small"
															value={getProductQuantityInput( part )}
															disabled={disabled}
															error={!!quantityError}
															helperText={quantityError ?? undefined}
															onChange={( e ) => setProductQuantity( part, e.target.value )}
															onBlur={() => touchProductQuantity( part )}
															slotProps={{
																htmlInput: {
																	'inputMode': 'numeric',
																	'aria-label': `Quantity to unload for ${part.partNumber || part.description}`,
																},
															}}
															sx={{ width: 140 }}
														/>
													</TableCell>
												</TableRow>
											)
										} )}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}

					<Divider sx={{ my: 4 }} />

					<Box sx={{ mb: 1.5 }}>
						<Stack direction="row" alignItems="center" spacing={1.5}>
							<Typography variant="subtitle1" fontWeight={700}>
								Transfer Orders
							</Typography>
							{anyInFlight && (
								<Stack direction="row" alignItems="center" spacing={0.75}>
									<Box
										sx={{
											'width': 8,
											'height': 8,
											'borderRadius': '50%',
											'bgcolor': 'info.main',
											'animation': 'mttPulse 1.4s ease-in-out infinite',
											'@keyframes mttPulse': {
												'0%, 100%': { opacity: 0.35 },
												'50%': { opacity: 1 },
											},
										}}
									/>
									<Typography variant="caption" color="text.secondary">
										Live — updating in-transit orders
									</Typography>
								</Stack>
							)}
						</Stack>
						<Typography variant="body2" color="text.secondary">
							{selectedDcId
								? `Loads and unloads between ${mttName} and ${destinationDcName}.`
								: `Unloads off ${mttName}. Select a Distribution Center in the header to focus on a specific DC and see its loads.`}
						</Typography>
					</Box>

					{toLoading && toEdges.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={22} />
						</Box>
					) : toEdges.length === 0 ? (
						<Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
							<Typography variant="body2" color="text.secondary">
								{selectedDcId
									? `No transfers yet between ${mttName} and ${destinationDcName}.`
									: `No transfer orders yet off ${mttName}.`}
							</Typography>
						</Paper>
					) : (
						<>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow sx={{ bgcolor: 'grey.50' }}>
											<TableHeaderCell>Transfer Order</TableHeaderCell>
											<TableHeaderCell>Direction</TableHeaderCell>
											<TableHeaderCell>Created</TableHeaderCell>
											<TableHeaderCell>Contents</TableHeaderCell>
											<TableHeaderCell>Status</TableHeaderCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{toEdges.map( ( edge ) => {
											const node: any = edge.node
											const assetTxns = node.assetTransactions ?? []
											const assetCount = assetTxns.length
											const assetsTransferred = assetTxns.filter( ( t: any ) => t.status === 'COMPLETED' ).length
											const assetsInFlight = assetTxns.filter( ( t: any ) => t.status === 'PENDING' ).length
											const assetsFailed = assetTxns.filter( ( t: any ) => t.status === 'FAILED' ).length
											const bulkUnits = ( node.lineItems ?? [] ).reduce(
												( sum: number, li: any ) => sum + Number( li.quantityRequested ?? 0 ),
												0,
											)
											// Inventory arriving at the trailer is a load; leaving it is an unload.
											const isLoad = String( node.toStore?.id ) === String( inventoryLocationId )
											const fromName = node.fromStore?.name ?? '—'
											const toName = node.toStore?.name ?? '—'
											return (
												<TableRow key={node.id} hover>
													<TableCell>
														<Link
															to={`/transfer-orders/${node.id}`}
															style={{ fontWeight: 600 }}
														>
															TO-{String( node.id ).slice( 0, 8 )}
														</Link>
													</TableCell>
													<TableCell>
														<Stack spacing={0.5} alignItems="flex-start">
															<DirectionChip isLoad={isLoad} />
															<Typography variant="caption" color="text.secondary">
																{fromName} → {toName}
															</Typography>
														</Stack>
													</TableCell>
													<TableCell>{formatDateTime( node.dateCreated )}</TableCell>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{assetCount} asset{assetCount === 1 ? '' : 's'} · {bulkUnits} bulk unit{bulkUnits === 1 ? '' : 's'}
														</Typography>
														{assetCount > 0 && ( assetsInFlight > 0 || assetsFailed > 0 ) && (
															<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
																{assetsTransferred} transferred{assetsInFlight > 0 ? ` · ${assetsInFlight} in-flight` : ''}{assetsFailed > 0 ? ` · ${assetsFailed} failed` : ''}
															</Typography>
														)}
													</TableCell>
													<TableCell>
														<Stack direction="row" spacing={1} alignItems="center">
															<StatusChip status={node.status} />
															{node.assetTransferStatus && (
																<AssetLegChip status={node.assetTransferStatus} />
															)}
														</Stack>
													</TableCell>
												</TableRow>
											)
										} )}
									</TableBody>
								</Table>
							</TableContainer>
							<Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 1 }}>
								<Button
									size="small"
									disabled={!toPageInfo?.hasPreviousPage}
									onClick={goPrevPage}
								>
									Previous
								</Button>
								<Button
									size="small"
									disabled={!toPageInfo?.hasNextPage}
									onClick={goNextPage}
								>
									Next
								</Button>
							</Stack>
						</>
					)}
				</>
			)}

			<Dialog open={confirmOpen} onClose={() => setConfirmOpen( false )} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>Unload to {destinationDcName}?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This creates a transfer order moving{' '}
						<strong>{selectedAssetIdList.length}</strong> asset{selectedAssetIdList.length === 1 ? '' : 's'}
						{' '}and <strong>{selectedProducts.length}</strong>{' '}
						bulk/consumable product{selectedProducts.length === 1 ? '' : 's'} from{' '}
						<strong>{mttName}</strong> to <strong>{destinationDcName}</strong>. Confirm these match
						what was physically counted. Anything not listed stays on the trailer.
					</DialogContentText>

					<TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 320 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableHeaderCell>Item</TableHeaderCell>
									<TableHeaderCell align="right">Qty</TableHeaderCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{selectedAssets.map( ( asset ) => (
									<TableRow key={`asset-${asset.id}`}>
										<TableCell>
											{asset.makeName} {asset.modelName} · {asset.name ?? asset.serialNumber ?? asset.id}
										</TableCell>
										<TableCell align="right">1</TableCell>
									</TableRow>
								) )}
								{selectedParts.map( ( { part, quantity } ) => (
									<TableRow key={`part-${part.productId}`}>
										<TableCell>
											{part.partNumber || '—'} · {part.description || '—'}
										</TableCell>
										<TableCell align="right">{quantity}</TableCell>
									</TableRow>
								) )}
							</TableBody>
						</Table>
					</TableContainer>

					{submitError && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{submitError}
						</Alert>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setConfirmOpen( false )} disabled={mutLoading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="warning"
						onClick={handleConfirm}
						disabled={!hasSelection || invalidCount > 0 || !selectedDcId || mutLoading}
						startIcon={mutLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{mutLoading ? 'Unloading…' : 'Yes, unload'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	)
}
